import React from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { X, Plus } from "lucide-react";
import { toast } from "sonner";
import * as api from "../../lib/api";
import { TemplatePicker } from "./TemplatePicker";
import { CONDITION_OPTIONS } from "./HandoverSection";
import { RebillFields, rebillStatus } from "./RebillFields";
import { SpecFieldInput } from "./SpecFieldInput";

export const emptyEquipmentForm = {
    category: "laptop", name: "", brand: "", model: "", serial_number: "", asset_tag: "",
    condition: "",
    purchase_date: "", purchase_price: "", purchase_currency: "CHF", supplier: "", invoice_number: "",
    warranty_start: "", warranty_end: "",
    rebill_status: "none", rebill_department: "", rebill_amount: "", rebill_date: "",
    extra_fields: [],
    specs: {},
};

export function buildEquipmentPayload(form) {
    return {
        ...form,
        purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
        rebill_amount: form.rebill_amount ? parseFloat(form.rebill_amount) : null,
        extra_fields: (form.extra_fields || []).filter((f) => (f.label || "").trim()).map(({ _key, ...f }) => f),
    };
}

export const itemToForm = (item) => ({
    ...emptyEquipmentForm,
    category: item.category || "laptop",
    name: item.name || "",
    brand: item.brand || "",
    model: item.model || "",
    serial_number: "",
    asset_tag: "",
    condition: item.condition || "",
    purchase_date: item.purchase_date || "",
    purchase_price: item.purchase_price != null ? String(item.purchase_price) : "",
    purchase_currency: item.purchase_currency || "CHF",
    supplier: item.supplier || "",
    invoice_number: item.invoice_number || "",
    warranty_start: item.warranty_start || "",
    warranty_end: item.warranty_end || "",
    rebill_status: rebillStatus(item),
    rebill_department: item.rebill_department || "",
    rebill_amount: item.rebill_amount != null ? String(item.rebill_amount) : "",
    rebill_date: item.rebill_date || "",
    extra_fields: (item.extra_fields || []).map((f, i) => ({ ...f, _key: `tpl-${i}` })),
    specs: { ...(item.specs || {}) },
});

export function ExtraFieldsEditor({ fields = [], onChange }) {
    const update = (idx, key, val) => onChange(fields.map((f, i) => (i === idx ? { ...f, [key]: val } : f)));
    const remove = (idx) => onChange(fields.filter((_, i) => i !== idx));
    return (
        <div className="space-y-2">
            {fields.map((f, idx) => (
                <div key={f._key ?? idx} className="flex items-center gap-2">
                    <Input value={f.label || ""} onChange={(e) => update(idx, "label", e.target.value)} placeholder="Nom du champ (ex: N° badge)" className="flex-1 h-8 text-xs" data-testid={`extra-field-label-${idx}`} />
                    <Input value={f.value || ""} onChange={(e) => update(idx, "value", e.target.value)} placeholder="Valeur" className="flex-1 h-8 text-xs" data-testid={`extra-field-value-${idx}`} />
                    <button type="button" onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 flex-shrink-0" title="Supprimer le champ" data-testid={`extra-field-remove-${idx}`}>
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ))}
            <button type="button" onClick={() => onChange([...fields, { label: "", value: "", _key: `ef-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` }])} className="inline-flex items-center gap-1 text-xs text-teal-600 hover:underline" data-testid="add-extra-field-btn">
                <Plus className="w-3 h-3" />Ajouter un champ
            </button>
        </div>
    );
}

export function EquipmentForm({ form, setForm, categories, allowTemplate = true }) {
    const set = (k, v) => setForm({ ...form, [k]: v });
    const selectedCat = categories.find((c) => c.key === form.category);

    const generateTag = async () => {
        try {
            const r = await api.getNextAssetTag(form.category);
            setForm({ ...form, asset_tag: r.asset_tag });
        } catch { toast.error("Erreur de génération du tag"); }
    };

    return (
        <div className="space-y-3">
            {allowTemplate && <TemplatePicker onPick={(i) => setForm(itemToForm(i))} />}
            <div className="grid grid-cols-2 gap-3">
                <div><label className="form-label text-xs">Catégorie *</label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, specs: {} })} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="eq-category">
                        {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="form-label text-xs">Asset Tag (identifiant)</label>
                    <div className="flex gap-1.5">
                        <Input value={form.asset_tag} onChange={(e) => set("asset_tag", e.target.value)} placeholder="EGG-NB-00001" data-testid="eq-tag" />
                        <Button type="button" variant="outline" size="sm" className="h-9 flex-shrink-0" onClick={generateTag} data-testid="eq-generate-tag">Générer</Button>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5">Laissez vide : généré automatiquement à la création</p>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div><label className="form-label text-xs">Nom (optionnel)</label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Dell Latitude 5540" data-testid="eq-name" /></div>
                <div><label className="form-label text-xs">N° série</label><Input value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} placeholder="SN-ABC123" data-testid="eq-serial" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div><label className="form-label text-xs">Marque</label><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="Dell" data-testid="eq-brand" /></div>
                <div><label className="form-label text-xs">Modèle</label><Input value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="Latitude 5540" data-testid="eq-model" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div><label className="form-label text-xs">État du matériel</label>
                    <select value={form.condition || ""} onChange={(e) => set("condition", e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="eq-condition">
                        {CONDITION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
            </div>
            {selectedCat?.custom_fields?.length > 0 && (
                <div className="border-t pt-3">
                    <p className="text-xs font-medium text-gray-600 mb-2">Spécifications ({selectedCat.label})</p>
                    <div className="grid grid-cols-2 gap-3">
                        {selectedCat.custom_fields.map((cf) => (
                            <div key={cf.key}><label className="form-label text-xs">{cf.label}</label>
                                <SpecFieldInput field={cf} value={form.specs[cf.key]} onChange={(v) => setForm({ ...form, specs: { ...form.specs, [cf.key]: v } })} placeholder={cf.label} testId={`eq-spec-${cf.key}`} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className="border-t pt-3">
                <p className="text-xs font-medium text-gray-600 mb-2">Achat & Facture</p>
                <div className="grid grid-cols-2 gap-3">
                    <div><label className="form-label text-xs">Date d'achat</label><Input type="date" value={form.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} data-testid="eq-purchase-date" /></div>
                    <div className="grid grid-cols-2 gap-2">
                        <div><label className="form-label text-xs">Prix</label><Input type="number" step="0.01" value={form.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} data-testid="eq-price" /></div>
                        <div><label className="form-label text-xs">Devise</label>
                            <select value={form.purchase_currency} onChange={(e) => set("purchase_currency", e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md">
                                <option value="CHF">CHF</option><option value="EUR">EUR</option><option value="USD">USD</option>
                            </select>
                        </div>
                    </div>
                    <div><label className="form-label text-xs">Fournisseur</label><Input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} data-testid="eq-supplier" /></div>
                    <div><label className="form-label text-xs">N° facture d'achat</label><Input value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} data-testid="eq-invoice" /></div>
                </div>
            </div>
            <div className="border-t pt-3">
                <p className="text-xs font-medium text-gray-600 mb-2">Garantie</p>
                <div className="grid grid-cols-2 gap-3">
                    <div><label className="form-label text-xs">Début</label><Input type="date" value={form.warranty_start} onChange={(e) => set("warranty_start", e.target.value)} data-testid="eq-warranty-start" /></div>
                    <div><label className="form-label text-xs">Fin</label><Input type="date" value={form.warranty_end} onChange={(e) => set("warranty_end", e.target.value)} data-testid="eq-warranty-end" /></div>
                </div>
            </div>
            <div className="border-t pt-3">
                <p className="text-xs font-medium text-gray-600 mb-2">Refacturation interne <span className="font-normal text-gray-400">(optionnel)</span></p>
                <RebillFields form={form} set={set} testPrefix="eq" />
            </div>
            <div className="border-t pt-3">
                <p className="text-xs font-medium text-gray-600 mb-2">Champs supplémentaires (au cas par cas)</p>
                <ExtraFieldsEditor fields={form.extra_fields || []} onChange={(v) => set("extra_fields", v)} />
            </div>
        </div>
    );
}

export function StockPicker({ items, value, onChange }) {
    const [q, setQ] = React.useState("");
    const filtered = items.filter((i) =>
        `${i.name} ${i.asset_tag} ${i.serial_number} ${i.brand} ${i.model}`.toLowerCase().includes(q.toLowerCase())
    );
    return (
        <div>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher dans le stock (tag, nom, SN)..." className="mb-2" data-testid="stock-picker-search" />
            <div className="border rounded-md max-h-52 overflow-y-auto divide-y divide-gray-50">
                {filtered.length === 0 && <p className="text-xs text-gray-400 p-3 text-center">Aucun matériel en stock</p>}
                {filtered.map((i) => (
                    <button key={i.id} type="button" onClick={() => onChange(i.id)}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-gray-50 ${value === i.id ? "bg-teal-50" : ""}`}
                        data-testid={`stock-item-${i.id}`}>
                        <span className="min-w-0">
                            <span className="font-medium text-gray-800">{i.asset_tag || i.name}</span>
                            <span className="text-xs text-gray-400 ml-2">{[i.name && i.asset_tag ? i.name : "", i.brand, i.model].filter(Boolean).join(" ")}{i.serial_number ? ` • SN ${i.serial_number}` : ""}</span>
                        </span>
                        {value === i.id && <span className="text-teal-600 text-xs font-medium flex-shrink-0">Sélectionné</span>}
                    </button>
                ))}
            </div>
        </div>
    );
}
